import * as React from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useMutation, useQuery } from "convex/react";
import { toast } from "sonner";
import {
  LayoutGrid,
  List,
  Plus,
  Upload,
  FileText,
  FolderPlus,
  ChevronDown,
} from "lucide-react";
import { DndContext, PointerSensor, useSensor, useSensors } from "@dnd-kit/core";
import type { DragEndEvent } from "@dnd-kit/core";
import { api } from "$convex/convexApi";
import { FolderTree } from "@/components/knowledgebase/FolderTree";
import { Breadcrumb } from "@/components/knowledgebase/Breadcrumb";
import { DocumentGrid } from "@/components/knowledgebase/DocumentGrid";
import { DocumentList } from "@/components/knowledgebase/DocumentList";
import { DocumentEditor } from "@/components/knowledgebase/DocumentEditor";
import { CreateFolderDialog } from "@/components/knowledgebase/CreateFolderDialog";
import { RenameFolderDialog } from "@/components/knowledgebase/RenameFolderDialog";
import { DeleteFolderDialog } from "@/components/knowledgebase/DeleteFolderDialog";
import { SearchAndFilterBar } from "@/components/knowledgebase/SearchAndFilterBar";
import { BulkActionsToolbar } from "@/components/knowledgebase/BulkActionsToolbar";
import { BulkActionConfirmDialog } from "@/components/knowledgebase/BulkActionConfirmDialog";
import { BulkMoveDialog } from "@/components/knowledgebase/BulkMoveDialog";

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useAuthIdentity } from "@/contexts/UserProfileContext";
import { KNOWLEDGEBASE_DEFAULT_DOCUMENTS } from "@/data/knowledgebase-defaults";

function normalizeFolderPath(value: string | undefined | null): string {
  if (!value) return "";
  return value
    .split("/")
    .map((segment) => segment.trim())
    .filter(Boolean)
    .join("/");
}

function buildDocumentRouteKey(document: { folderPath?: string; slug: string }): string {
  const folderPath = normalizeFolderPath(document.folderPath);
  return folderPath ? `${folderPath}/${document.slug}` : document.slug;
}

function encodeRouteKey(value: string): string {
  return value
    .split("/")
    .map((segment) => encodeURIComponent(segment))
    .join("/");
}

function decodeRouteKey(pathname: string): string {
  const basePath = "/admin/knowledgebase";
  if (pathname === basePath) return "";
  if (!pathname.startsWith(`${basePath}/`)) return "";
  const encoded = pathname.slice(basePath.length + 1);
  if (!encoded) return "";
  return encoded
    .split("/")
    .map((segment) => decodeURIComponent(segment))
    .join("/");
}

function sanitizeEmbeddedSourceMarkdown(markdown: string): string {
  const EMBEDDED_LOCAL_SOURCE_LINE_PATTERN =
    /^(Source(?: Folder)?|Path):\s*`(\/Users\/[^`]+|~\/[^`]+|[a-zA-Z]:[\\/][^`]+)`/gm;
  const filenameFromPath = (input: string): string => {
    const segments = input.split(/[\\/]/).filter(Boolean);
    return segments[segments.length - 1] ?? input;
  };
  if (!markdown) return "";
  return markdown.replace(EMBEDDED_LOCAL_SOURCE_LINE_PATTERN, (_full, label, sourcePath) => {
    return `${label}: \`${filenameFromPath(sourcePath)}\``;
  });
}

export default function AdminKnowledgebasePage() {
  const { authUserId, userRole } = useAuthIdentity();
  const location = useLocation();
  const navigate = useNavigate();

  const canManageKnowledgebase = Boolean(authUserId && userRole === "admin");

  const [selectedDocumentId, setSelectedDocumentId] = React.useState<string | null>(null);
  const [selectedFolderPath, setSelectedFolderPath] = React.useState<string>("");
  const [viewMode, setViewMode] = React.useState<"grid" | "list">(() => {
    try {
      const stored = localStorage.getItem("knowledgebase:viewMode");
      return stored === "list" ? "list" : "grid";
    } catch {
      return "grid";
    }
  });
  const [importingDefaults, setImportingDefaults] = React.useState(false);

  const [searchQuery, setSearchQuery] = React.useState("");
  const [statusFilter, setStatusFilter] = React.useState<string | undefined>(undefined);
  const [categoryFilter, setCategoryFilter] = React.useState<string | undefined>(undefined);
  const [tagsFilter, setTagsFilter] = React.useState<string[]>([]);
  const [isSearchMode, setIsSearchMode] = React.useState(false);

  const [createFolderOpen, setCreateFolderOpen] = React.useState(false);
  const [createFolderParentPath, setCreateFolderParentPath] = React.useState<string | undefined>(undefined);
  const [renameFolderOpen, setRenameFolderOpen] = React.useState(false);
  const [renameFolderPath, setRenameFolderPath] = React.useState("");
  const [renameFolderName, setRenameFolderName] = React.useState("");
  const [deleteFolderOpen, setDeleteFolderOpen] = React.useState(false);
  const [deleteFolderPath, setDeleteFolderPath] = React.useState("");
  const [deleteFolderName, setDeleteFolderName] = React.useState("");

  const [selectedDocumentIds, setSelectedDocumentIds] = React.useState<Set<string>>(new Set());
  const [bulkArchiveDialogOpen, setBulkArchiveDialogOpen] = React.useState(false);
  const [bulkDeleteDialogOpen, setBulkDeleteDialogOpen] = React.useState(false);
  const [bulkMoveDialogOpen, setBulkMoveDialogOpen] = React.useState(false);
  const [bulkStatusChangeValue, setBulkStatusChangeValue] = React.useState<"draft" | "published" | "archived" | null>(null);

  const listArgs = React.useMemo(() => {
    if (!canManageKnowledgebase || !authUserId) return "skip" as const;
    return {
      authUserId,
      includeContent: false,
      limit: 500,
    };
  }, [authUserId, canManageKnowledgebase]);

  const listResult = useQuery(api.knowledgeBase.listDocuments, listArgs);

  const folderContents = useQuery(
    api.knowledgeBase.getFolderContents,
    canManageKnowledgebase && authUserId && !selectedDocumentId && !isSearchMode
      ? {
          authUserId,
          folderPath: selectedFolderPath || undefined,
        }
      : "skip",
  );

  const searchResults = useQuery(
    api.knowledgeBase.searchDocuments,
    canManageKnowledgebase && authUserId && !selectedDocumentId && isSearchMode
      ? {
          authUserId,
          query: searchQuery || undefined,
          status: statusFilter as "draft" | "published" | "archived" | undefined,
          category: categoryFilter || undefined,
          tags: tagsFilter.length > 0 ? tagsFilter : undefined,
        }
      : "skip",
  );

  React.useEffect(() => {
    try {
      localStorage.setItem("knowledgebase:viewMode", viewMode);
    } catch {
      // Ignore storage errors
    }
  }, [viewMode]);

  const bulkUpsertDocuments = useMutation(api.knowledgeBase.bulkUpsertDocuments);
  const bulkUpdateStatus = useMutation(api.knowledgeBase.bulkUpdateStatus);
  const bulkMoveDocuments = useMutation(api.knowledgeBase.bulkMoveDocuments);
  const bulkDeleteDocuments = useMutation(api.knowledgeBase.bulkDeleteDocuments);
  const moveDocument = useMutation(api.knowledgeBase.moveDocument);

  const documents = (listResult?.documents ?? []) as any[];
  const selectedRouteKey = React.useMemo(() => decodeRouteKey(location.pathname), [location.pathname]);
  const navigateToFolder = React.useCallback((folderPath: string) => {
    const normalizedFolderPath = normalizeFolderPath(folderPath);
    const targetPath = normalizedFolderPath
      ? `/admin/knowledgebase/${encodeRouteKey(normalizedFolderPath)}`
      : "/admin/knowledgebase";

    if (location.pathname === targetPath) return;
    navigate(targetPath);
  }, [location.pathname, navigate]);

  const navigateToDocument = React.useCallback((document: { folderPath?: string; slug: string }) => {
    const routeKey = buildDocumentRouteKey(document);
    const targetPath = `/admin/knowledgebase/${encodeRouteKey(routeKey)}`;
    if (location.pathname === targetPath) return;
    navigate(targetPath);
  }, [location.pathname, navigate]);

  React.useEffect(() => {
    if (!selectedRouteKey) {
      setSelectedFolderPath("");
      setSelectedDocumentId(null);
      return;
    }

    if (documents.length === 0) return;

    const routeDocument = documents.find(
      (document) => buildDocumentRouteKey(document) === selectedRouteKey,
    );
    if (routeDocument) {
      setSelectedFolderPath(normalizeFolderPath(routeDocument.folderPath));
      setSelectedDocumentId(String(routeDocument._id));
      return;
    }

    const normalizedFolderRoute = normalizeFolderPath(selectedRouteKey);
    const isFolderPath = documents.some((document) => {
      const documentFolderPath = normalizeFolderPath(document.folderPath);
      return (
        documentFolderPath === normalizedFolderRoute ||
        documentFolderPath.startsWith(`${normalizedFolderRoute}/`)
      );
    });

    if (isFolderPath) {
      setSelectedFolderPath(normalizedFolderRoute);
      setSelectedDocumentId(null);
      return;
    }

    navigateToFolder("");
  }, [documents, navigateToFolder, selectedRouteKey]);

  const onCreateNew = React.useCallback((folderPath: string) => {
    navigateToFolder(folderPath);
    setSelectedDocumentId(null);
  }, [navigateToFolder]);

  const onImportDefaults = React.useCallback(async () => {
    if (!authUserId || !canManageKnowledgebase) {
      toast.error("Admin access required");
      return;
    }

    setImportingDefaults(true);
    try {
      const result = await bulkUpsertDocuments({
        authUserId,
        documents: KNOWLEDGEBASE_DEFAULT_DOCUMENTS.map((document) => ({
          ...document,
          contentMarkdown: sanitizeEmbeddedSourceMarkdown(document.contentMarkdown),
        })),
      });
      toast.success(
        `Imported ${result.total} documents (${result.created} created, ${result.updated} updated).`,
      );
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Failed to import defaults");
    } finally {
      setImportingDefaults(false);
    }
  }, [authUserId, bulkUpsertDocuments, canManageKnowledgebase]);


  const onSelectFolder = React.useCallback((folderPath: string) => {
    navigateToFolder(folderPath);
  }, [navigateToFolder]);

  const onNavigateToBreadcrumb = React.useCallback((folderPath: string) => {
    navigateToFolder(folderPath);
  }, [navigateToFolder]);

  const onDocumentClick = React.useCallback((documentId: string) => {
    const selectedDocument = documents.find((document) => String(document._id) === documentId);
    if (!selectedDocument) {
      setSelectedDocumentId(documentId);
      return;
    }

    setSelectedFolderPath(normalizeFolderPath(selectedDocument.folderPath));
    setSelectedDocumentId(String(selectedDocument._id));
    navigateToDocument(selectedDocument);
  }, [documents, navigateToDocument]);

  const onCloseEditor = React.useCallback(() => {
    setSelectedDocumentId(null);
    navigateToFolder(selectedFolderPath);
  }, [navigateToFolder, selectedFolderPath]);

  const onFolderClick = React.useCallback((folderPath: string) => {
    navigateToFolder(folderPath);
  }, [navigateToFolder]);

  const onToggleViewMode = React.useCallback(() => {
    setViewMode((prev) => (prev === "grid" ? "list" : "grid"));
  }, []);

  const handleNewDocument = React.useCallback((folderPath?: string) => {
    onCreateNew(folderPath ?? selectedFolderPath);
  }, [onCreateNew, selectedFolderPath]);

  const handleNewFolder = React.useCallback((parentPath?: string) => {
    setCreateFolderParentPath(parentPath || selectedFolderPath || undefined);
    setCreateFolderOpen(true);
  }, [selectedFolderPath]);

  const handleRenameFolder = React.useCallback((folderPath: string, currentName: string) => {
    setRenameFolderPath(folderPath);
    setRenameFolderName(currentName);
    setRenameFolderOpen(true);
  }, []);

  const handleDeleteFolder = React.useCallback((folderPath: string, folderName: string) => {
    setDeleteFolderPath(folderPath);
    setDeleteFolderName(folderName);
    setDeleteFolderOpen(true);
  }, []);

  const handleFolderOperationSuccess = React.useCallback(() => {
    // Folder tree will automatically refresh via Convex reactivity
  }, []);

  React.useEffect(() => {
    const hasActiveSearch = Boolean(searchQuery || statusFilter || categoryFilter || tagsFilter.length > 0);
    setIsSearchMode(hasActiveSearch);
  }, [searchQuery, statusFilter, categoryFilter, tagsFilter]);

  const handleSearchChange = React.useCallback((query: string) => {
    setSearchQuery(query);
  }, []);

  const handleClearFilters = React.useCallback(() => {
    setSearchQuery("");
    setStatusFilter(undefined);
    setCategoryFilter(undefined);
    setTagsFilter([]);
  }, []);

  const availableCategories = React.useMemo(() => {
    const categories = new Set<string>();
    documents.forEach((doc: any) => {
      if (doc.category) categories.add(doc.category);
    });
    return Array.from(categories).sort();
  }, [documents]);

  const availableTags = React.useMemo(() => {
    const tags = new Set<string>();
    documents.forEach((doc: any) => {
      if (Array.isArray(doc.tags)) {
        doc.tags.forEach((tag: string) => tags.add(tag));
      }
    });
    return Array.from(tags).sort();
  }, [documents]);

  const hasActiveFilters = Boolean(statusFilter || categoryFilter || tagsFilter.length > 0);

  const displayDocuments = isSearchMode ? (searchResults?.documents ?? []) : (folderContents?.documents ?? []);
  const displaySubfolders = isSearchMode ? [] : (folderContents?.subfolders ?? []);
  const resultCount = isSearchMode ? (searchResults?.totalResults ?? 0) : displayDocuments.length;

  const handleDocumentSelectionChange = React.useCallback((documentId: string, selected: boolean) => {
    setSelectedDocumentIds((prev) => {
      const next = new Set(prev);
      if (selected) {
        next.add(documentId);
      } else {
        next.delete(documentId);
      }
      return next;
    });
  }, []);

  const handleSelectAll = React.useCallback((selected: boolean) => {
    if (selected) {
      setSelectedDocumentIds(new Set(displayDocuments.map((doc: any) => doc._id)));
    } else {
      setSelectedDocumentIds(new Set());
    }
  }, [displayDocuments]);

  const handleDeselectAll = React.useCallback(() => {
    setSelectedDocumentIds(new Set());
  }, []);

  const handleBulkArchive = React.useCallback(() => {
    setBulkArchiveDialogOpen(true);
  }, []);

  const handleBulkDelete = React.useCallback(() => {
    setBulkDeleteDialogOpen(true);
  }, []);

  const handleBulkChangeStatus = React.useCallback((status: "draft" | "published" | "archived") => {
    setBulkStatusChangeValue(status);
  }, []);

  const handleBulkMoveToFolder = React.useCallback(() => {
    setBulkMoveDialogOpen(true);
  }, []);

  const confirmBulkArchive = React.useCallback(async () => {
    if (!authUserId || selectedDocumentIds.size === 0) return;

    try {
      const result = await bulkUpdateStatus({
        authUserId,
        documentIds: Array.from(selectedDocumentIds) as any[],
        status: "archived",
      });
      toast.success(result.message);
      setSelectedDocumentIds(new Set());
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Failed to archive documents");
    }
  }, [authUserId, bulkUpdateStatus, selectedDocumentIds]);

  const confirmBulkDelete = React.useCallback(async () => {
    if (!authUserId || selectedDocumentIds.size === 0) return;

    try {
      const result = await bulkDeleteDocuments({
        authUserId,
        documentIds: Array.from(selectedDocumentIds) as any[],
        hardDelete: false,
      });
      toast.success(result.message);
      setSelectedDocumentIds(new Set());
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Failed to delete documents");
    }
  }, [authUserId, bulkDeleteDocuments, selectedDocumentIds]);

  const confirmBulkStatusChange = React.useCallback(async () => {
    if (!authUserId || selectedDocumentIds.size === 0 || !bulkStatusChangeValue) return;

    try {
      const result = await bulkUpdateStatus({
        authUserId,
        documentIds: Array.from(selectedDocumentIds) as any[],
        status: bulkStatusChangeValue,
      });
      toast.success(result.message);
      setSelectedDocumentIds(new Set());
      setBulkStatusChangeValue(null);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Failed to update documents");
    }
  }, [authUserId, bulkUpdateStatus, bulkStatusChangeValue, selectedDocumentIds]);

  const handleBulkMoveSuccess = React.useCallback(() => {
    setSelectedDocumentIds(new Set());
  }, []);

  const handleDragEnd = React.useCallback(async (event: DragEndEvent) => {
    const { active, over } = event;

    if (!over) return;
    if (!authUserId) return;

    const dragData = active.data.current;
    const dropData = over.data.current;

    if (!dragData || dragData.type !== "document" || !dragData.documentId) return;
    if (!dropData || !dropData.folderPath) return;

    const newFolderPath = dropData.folderPath;

    try {
      const result = await moveDocument({
        authUserId,
        documentId: dragData.documentId,
        newFolderPath,
      });
      toast.success(result.message);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Failed to move document");
    }
  }, [authUserId, moveDocument]);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
  );

  return (
    <DndContext sensors={sensors} onDragEnd={handleDragEnd}>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Knowledgebase</h1>
          <p className="mt-1 text-muted-foreground">
            Folder-based internal docs. Use the left sidebar tree to browse folders and subpages.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" onClick={onImportDefaults} disabled={importingDefaults}>
            <Upload className="mr-2 size-4" />
            {importingDefaults ? "Importing..." : "Import Defaults"}
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button>
                <Plus className="mr-2 size-4" />
                New
                <ChevronDown className="ml-2 size-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => handleNewDocument()}>
                <FileText className="size-4" />
                New Document
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => handleNewFolder()}>
                <FolderPlus className="size-4" />
                New Folder
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {!canManageKnowledgebase ? (
        <Card className="border-amber-300 bg-amber-50/60 dark:border-amber-800/60 dark:bg-amber-950/25">
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Admin required</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground">
              Knowledgebase management requires admin access.
            </p>
          </CardContent>
        </Card>
      ) : null}

      <div className="grid grid-cols-[280px_1fr] gap-6">
        {canManageKnowledgebase && authUserId ? (
          <Card className="h-fit">
            <CardHeader className="pb-3">
              <CardTitle className="text-base">Folders</CardTitle>
              <CardDescription className="text-xs">
                Navigate the document hierarchy
              </CardDescription>
            </CardHeader>
            <CardContent className="p-0">
              <FolderTree
                authUserId={authUserId}
                selectedPath={selectedFolderPath}
                onSelectFolder={onSelectFolder}
                onNewDocument={handleNewDocument}
                onNewFolder={handleNewFolder}
                onRenameFolder={handleRenameFolder}
                onDeleteFolder={handleDeleteFolder}
              />
            </CardContent>
          </Card>
        ) : null}

        <div className="grid gap-6">
          {selectedDocumentId && canManageKnowledgebase && authUserId ? (
            <Card>
              <CardContent className="pt-6">
                <DocumentEditor
                  authUserId={authUserId}
                  documentId={selectedDocumentId}
                  onClose={onCloseEditor}
                />
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4">
              <SearchAndFilterBar
                searchQuery={searchQuery}
                onSearchChange={handleSearchChange}
                statusFilter={statusFilter}
                onStatusFilterChange={setStatusFilter}
                categoryFilter={categoryFilter}
                onCategoryFilterChange={setCategoryFilter}
                tagsFilter={tagsFilter}
                onTagsFilterChange={setTagsFilter}
                availableCategories={availableCategories}
                availableTags={availableTags}
                resultCount={resultCount}
                hasActiveFilters={hasActiveFilters}
                onClearFilters={handleClearFilters}
              />

              {!isSearchMode && (
                <div className="flex items-center justify-between">
                  <Breadcrumb
                    folderPath={selectedFolderPath}
                    onNavigate={onNavigateToBreadcrumb}
                  />
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={onToggleViewMode}
                  >
                    {viewMode === "grid" ? (
                      <>
                        <List className="mr-2 size-4" />
                        List View
                      </>
                    ) : (
                      <>
                        <LayoutGrid className="mr-2 size-4" />
                        Grid View
                      </>
                    )}
                  </Button>
                </div>
              )}

              {isSearchMode && (
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <span className="font-medium">Search Results</span>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={onToggleViewMode}
                  >
                    {viewMode === "grid" ? (
                      <>
                        <List className="mr-2 size-4" />
                        List View
                      </>
                    ) : (
                      <>
                        <LayoutGrid className="mr-2 size-4" />
                        Grid View
                      </>
                    )}
                  </Button>
                </div>
              )}

              {displayDocuments.length === 0 && displaySubfolders.length === 0 ? (
                <Card>
                  <CardContent className="flex flex-col items-center justify-center py-12 text-center">
                    <p className="text-lg font-medium text-muted-foreground">
                      {isSearchMode ? "No documents found" : "This folder is empty"}
                    </p>
                    <p className="mt-1 text-sm text-muted-foreground">
                      {isSearchMode ? "Try adjusting your search or filters" : "Create a new document to get started"}
                    </p>
                  </CardContent>
                </Card>
              ) : viewMode === "grid" ? (
                <DocumentGrid
                  documents={displayDocuments}
                  subfolders={displaySubfolders}
                  onDocumentClick={onDocumentClick}
                  onFolderClick={onFolderClick}
                  selectedDocumentIds={selectedDocumentIds}
                  onDocumentSelectionChange={handleDocumentSelectionChange}
                />
              ) : (
                <DocumentList
                  documents={displayDocuments}
                  subfolders={displaySubfolders}
                  onDocumentClick={onDocumentClick}
                  onFolderClick={onFolderClick}
                  selectedDocumentIds={selectedDocumentIds}
                  onDocumentSelectionChange={handleDocumentSelectionChange}
                  onSelectAll={handleSelectAll}
                />
              )}
            </div>
          )}
        </div>
      </div>

      {canManageKnowledgebase && authUserId ? (
        <>
          <CreateFolderDialog
            authUserId={authUserId}
            parentPath={createFolderParentPath}
            open={createFolderOpen}
            onOpenChange={setCreateFolderOpen}
            onSuccess={handleFolderOperationSuccess}
          />
          <RenameFolderDialog
            authUserId={authUserId}
            folderPath={renameFolderPath}
            currentName={renameFolderName}
            open={renameFolderOpen}
            onOpenChange={setRenameFolderOpen}
            onSuccess={handleFolderOperationSuccess}
          />
          <DeleteFolderDialog
            authUserId={authUserId}
            folderPath={deleteFolderPath}
            folderName={deleteFolderName}
            open={deleteFolderOpen}
            onOpenChange={setDeleteFolderOpen}
            onSuccess={handleFolderOperationSuccess}
          />

          <BulkActionsToolbar
            selectedCount={selectedDocumentIds.size}
            onArchive={handleBulkArchive}
            onDelete={handleBulkDelete}
            onChangeStatus={handleBulkChangeStatus}
            onMoveToFolder={handleBulkMoveToFolder}
            onDeselectAll={handleDeselectAll}
          />

          <BulkActionConfirmDialog
            open={bulkArchiveDialogOpen}
            onOpenChange={setBulkArchiveDialogOpen}
            title="Archive documents"
            description="Are you sure you want to archive the selected documents? Archived documents can be restored later."
            actionLabel="Archive"
            actionVariant="default"
            onConfirm={confirmBulkArchive}
            selectedCount={selectedDocumentIds.size}
          />

          <BulkActionConfirmDialog
            open={bulkDeleteDialogOpen}
            onOpenChange={setBulkDeleteDialogOpen}
            title="Delete documents"
            description="Are you sure you want to delete the selected documents? This action will archive the documents."
            actionLabel="Delete"
            actionVariant="destructive"
            onConfirm={confirmBulkDelete}
            selectedCount={selectedDocumentIds.size}
          />

          <BulkActionConfirmDialog
            open={bulkStatusChangeValue !== null}
            onOpenChange={(open) => !open && setBulkStatusChangeValue(null)}
            title={`Change status to ${bulkStatusChangeValue}`}
            description={`Are you sure you want to change the status of the selected documents to ${bulkStatusChangeValue}?`}
            actionLabel="Change status"
            actionVariant="default"
            onConfirm={confirmBulkStatusChange}
            selectedCount={selectedDocumentIds.size}
          />

          <BulkMoveDialog
            authUserId={authUserId}
            open={bulkMoveDialogOpen}
            onOpenChange={setBulkMoveDialogOpen}
            selectedDocumentIds={Array.from(selectedDocumentIds)}
            onSuccess={handleBulkMoveSuccess}
          />
        </>
      ) : null}
      </div>
    </DndContext>
  );
}
